import { useState } from "react";
import { useForm } from "@inertiajs/react";
import { Target, Pencil, Check } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import FormField from "@/components/forms/FormField";
import ProgressBar from "@/components/shared/ProgressBar";
import { formatMoney, formatNumber, percentOf } from "@/lib/format";

/**
 * Goals a parent sets with their child: minutes, money, or both. With
 * none set it's a single row inviting one.
 */
export default function PersonalGoalsCard({ student }) {
  const minuteGoal = student.personal_minute_goal;
  const moneyGoal = student.personal_money_goal_cents;
  const hasGoals = Boolean(minuteGoal || moneyGoal);
  const [editing, setEditing] = useState(false);

  if (!hasGoals && !editing) {
    return (
      <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-5 sm:flex-row sm:items-center">
        <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Target className="h-4 w-4" />
        </span>
        <p className="flex-1 font-medium text-foreground">Set a goal with {student.first_name}</p>
        <Button variant="outline" size="sm" onClick={() => setEditing(true)}>Set a goal</Button>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0">
        <CardTitle className="flex items-center gap-2 text-xl">
          <Target className="h-5 w-5 text-primary" />
          {student.first_name}'s goals
        </CardTitle>
        {!editing && (
          <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
            <Pencil className="h-4 w-4" /> Edit
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {editing ? (
          <GoalsForm student={student} onDone={() => setEditing(false)} />
        ) : (
          <div className="space-y-5">
            {minuteGoal && (
              <ProgressBar
                label="Minutes read"
                detail={`${formatNumber(student.minutes_logged)} / ${formatNumber(minuteGoal)}`}
                pct={percentOf(student.minutes_logged, minuteGoal)}
                barColor="bg-accent"
              />
            )}
            {moneyGoal && (
              <ProgressBar
                label="Raised"
                detail={`${formatMoney(student.raised_cents)} / ${formatMoney(moneyGoal)}`}
                pct={percentOf(student.raised_cents, moneyGoal)}
              />
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function GoalsForm({ student, onDone }) {
  const { data, setData, patch, processing, errors } = useForm({
    personal_minute_goal: student.personal_minute_goal ?? "",
    personal_money_goal_dollars: student.personal_money_goal_cents != null ? String(student.personal_money_goal_cents / 100) : "",
  });

  const submit = (e) => {
    e.preventDefault();
    patch(student.update_goals_path, { preserveScroll: true, onSuccess: onDone });
  };

  return (
    <form onSubmit={submit} className="space-y-5">
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField
          name="personal_minute_goal"
          label="Minutes to read"
          type="number"
          inputMode="numeric"
          min="1"
          placeholder="500"
          value={data.personal_minute_goal}
          onChange={(e) => setData("personal_minute_goal", e.target.value)}
          error={errors.personal_minute_goal}
        />
        <FormField
          name="personal_money_goal_dollars"
          label="Dollars to raise"
          type="number"
          inputMode="numeric"
          min="0"
          placeholder="100"
          value={data.personal_money_goal_dollars}
          onChange={(e) => setData("personal_money_goal_dollars", e.target.value)}
          error={errors.personal_money_goal_cents}
        />
      </div>
      <div className="flex items-center gap-2">
        <Button type="submit" variant="secondary" size="sm" disabled={processing}>
          <Check className="h-4 w-4" />
          {processing ? "Saving…" : "Save goals"}
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onDone}>Cancel</Button>
      </div>
    </form>
  );
}
